// Locomotive Scroll v5 Optimized Implementation
// Import as global since the minified version uses UMD format
import './locomotive-scroll.min.js';
const LocomotiveScroll = window.LocomotiveScroll;

class LocomotiveScrollManager {
  constructor() {
    this.locomotiveScroll = null;
    this.isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.isDesignMode = this.detectDesignMode();
    this.performanceMonitor = null;
    this.frameCount = 0;
    this.lastTime = 0;
    this.isSSR = typeof window === 'undefined';
    this.designModeObserver = null;
  }

  detectDesignMode() {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return false;
    }

    // Multiple methods to detect Shopify design mode
    const designModeIndicators = [
      // Primary indicator: HTML class
      document.documentElement.classList.contains('shopify-design-mode'),
      
      // URL parameter indicator
      window.location.search.includes('preview_theme_id='),
      
      // Theme editor specific URLs
      window.location.pathname.includes('/admin/themes/'),
      
      // Check for theme editor specific elements
      document.querySelector('[data-shopify-editor-section]') !== null,
      
      // Check for design mode meta tag
      document.querySelector('meta[name="shopify-design-mode"]') !== null,
      
      // Check for request.design_mode in URL
      window.location.search.includes('design_mode=true')
    ];

    return designModeIndicators.some(indicator => indicator === true);
  }

  init() {
    // SSR safety check
    if (this.isSSR) {
      console.warn('Locomotive Scroll: Disabled in SSR environment');
      return;
    }

    // Re-check design mode at initialization (in case it changed)
    this.isDesignMode = this.detectDesignMode();

    // Don't initialize if user prefers reduced motion
    if (this.prefersReducedMotion) {
      console.log('Locomotive Scroll: Disabled due to reduced motion preference');
      return;
    }

    // Don't initialize in design mode for better theme editor performance
    if (this.isDesignMode) {
      console.log('Locomotive Scroll: Disabled in Shopify design mode');
      this.setupDesignModeObserver();
      return;
    }

    // Initialize with optimized settings
    this.locomotiveScroll = new LocomotiveScroll({
      lenisOptions: {
        wrapper: window,
        content: document.documentElement,
        lerp: 0.1, // Smooth interpolation
        duration: 1.2, // Animation duration
        orientation: 'vertical',
        gestureOrientation: 'vertical',
        smoothWheel: true,
        smoothTouch: false, // Disabled for better touch performance
        wheelMultiplier: 1,
        touchMultiplier: 2,
        normalizeWheel: true,
        easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      },
      // Intersection Observer settings for better performance
      triggerRootMargin: '-1px -1px -1px -1px',
      rafRootMargin: '100% 100% 100% 100%',
      autoResize: true,
      autoStart: true,
      
      // Scroll callback for custom events
      scrollCallback: ({ scroll, limit, velocity, direction, progress }) => {
        // Dispatch custom event for other scripts to listen to
        window.dispatchEvent(new CustomEvent('locomotive-scroll', {
          detail: { scroll, limit, velocity, direction, progress }
        }));
      },
    });

    // Handle third-party modal conflicts (common in Shopify themes)
    this.handleModalConflicts();
    
    // Handle page transitions if enabled
    this.handlePageTransitions();
    
    // Handle resize events
    this.handleResize();

    // Initialize performance monitoring (only if not in design mode)
    if (!this.isDesignMode) {
      this.initPerformanceMonitoring();
    }

    // Set up design mode observer even after successful initialization
    // (to handle entering design mode after init)
    this.setupDesignModeObserver();

    console.log('Locomotive Scroll: Initialized successfully');
  }

  handleModalConflicts() {
    if (!this.locomotiveScroll?.lenisInstance) return;

    // Enhanced modal selectors for Shopify themes and common third-party plugins
    const modalSelectors = [
      '[data-modal]',
      '[data-dialog]',
      '.modal',
      '.drawer',
      '[data-drawer]',
      '.quickview',
      '[data-quickview]',
      '.cart-drawer',
      '.search-modal',
      '.popup',
      '[data-popup]',
      '.overlay',
      '[data-overlay]',
      '.lightbox',
      '[data-lightbox]',
      '.fancybox-container',
      '.magnific-popup',
      '[data-lenis-prevent]',
      '.shopify-chat',
      '[data-shopify-chat]',
      '.customer-chat',
      '.live-chat',
      '.zendesk-widget',
      '.intercom-messenger',
      '.klaviyo-form',
      '[data-embed]',
      '.video-modal'
    ];

    // Enhanced modal conflict handling with touch events
    const preventScrollEvents = ['wheel', 'touchmove'];
    
    preventScrollEvents.forEach(eventType => {
      this.locomotiveScroll.lenisInstance.options.content.addEventListener(eventType, (event) => {
        const paths = event.composedPath();
        
        const hasModal = paths.find((el) => {
          if (!(el instanceof HTMLElement)) return false;
          
          // Check for data-lenis-prevent attribute first (fastest check)
          if (el.hasAttribute?.('data-lenis-prevent')) return true;
          
          return modalSelectors.some(selector => {
            try {
              return el.matches?.(selector) || el.closest?.(selector);
            } catch (e) {
              return false;
            }
          });
        });

        if (hasModal) {
          event.stopPropagation();
        }
      }, { passive: false });
    });
  }

  handlePageTransitions() {
    // Handle Shopify's view transitions if enabled
    const mainContent = document.getElementById('MainContent');
    if (!mainContent) return;

    const isTransitionEnabled = mainContent.dataset.pageTransitionEnabled === 'true';
    
    if (isTransitionEnabled) {
      // Refresh scroll on page transition
      document.addEventListener('astro:page-load', () => {
        this.refresh();
      });
    }
  }

  handleResize() {
    let resizeTimer;
    
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        this.refresh();
      }, 250);
    });
  }

  refresh() {
    if (this.locomotiveScroll) {
      // Use requestAnimationFrame for better performance
      requestAnimationFrame(() => {
        this.locomotiveScroll.resize();
      });
    }
  }

  scrollTo(target, options = {}) {
    if (!this.locomotiveScroll) return;

    const defaultOptions = {
      offset: 0,
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      ...options
    };

    this.locomotiveScroll.scrollTo(target, defaultOptions);
  }

  initPerformanceMonitoring() {
    if (this.isDesignMode || !console.time) return;

    this.performanceMonitor = {
      start: performance.now(),
      frameStart: performance.now()
    };

    // Monitor performance every 60 frames
    const monitorPerformance = () => {
      this.frameCount++;
      
      if (this.frameCount % 60 === 0) {
        const now = performance.now();
        const fps = 1000 / (now - this.lastTime) * 60;
        
        if (fps < 30) {
          console.warn(`Locomotive Scroll: Performance warning - FPS: ${fps.toFixed(1)}`);
        }
        
        this.lastTime = now;
      }
      
      if (this.locomotiveScroll) {
        requestAnimationFrame(monitorPerformance);
      }
    };

    requestAnimationFrame(monitorPerformance);
  }

  addScrollElements(container) {
    if (!this.locomotiveScroll || !container) return;
    
    try {
      this.locomotiveScroll.addScrollElements(container);
      console.log('Locomotive Scroll: Added scroll elements for container');
    } catch (error) {
      console.error('Locomotive Scroll: Error adding scroll elements:', error);
    }
  }

  removeScrollElements(container) {
    if (!this.locomotiveScroll || !container) return;
    
    try {
      this.locomotiveScroll.removeScrollElements(container);
      console.log('Locomotive Scroll: Removed scroll elements for container');
    } catch (error) {
      console.error('Locomotive Scroll: Error removing scroll elements:', error);
    }
  }


  setupDesignModeObserver() {
    // Watch for changes that might indicate leaving design mode
    // Prevent multiple observers
    if (!this.designModeObserver && window.MutationObserver) {
      this.designModeObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
            const wasInDesignMode = this.isDesignMode;
            this.isDesignMode = this.detectDesignMode();
            
            // If we left design mode, try to initialize locomotive scroll
            if (wasInDesignMode && !this.isDesignMode) {
              console.log('Locomotive Scroll: Design mode exited, initializing...');
              setTimeout(() => {
                this.init();
              }, 100);
            }
            // If we entered design mode, destroy locomotive scroll properly
            else if (!wasInDesignMode && this.isDesignMode) {
              console.log('Locomotive Scroll: Design mode entered, destroying...');
              if (this.locomotiveScroll) {
                this.locomotiveScroll.destroy();
                this.locomotiveScroll = null;
              }
            }
          }
        });
      });

      // Observe the HTML element for class changes
      this.designModeObserver.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['class']
      });

      console.log('Locomotive Scroll: Design mode observer active');
    }

    // Also listen for URL changes (for SPA-like behavior)
    if (window.addEventListener) {
      window.addEventListener('popstate', () => {
        const wasInDesignMode = this.isDesignMode;
        this.isDesignMode = this.detectDesignMode();
        
        if (wasInDesignMode && !this.isDesignMode) {
          console.log('Locomotive Scroll: URL change detected, design mode exited');
          setTimeout(() => {
            this.init();
          }, 100);
        } else if (!wasInDesignMode && this.isDesignMode) {
          console.log('Locomotive Scroll: URL change detected, design mode entered');
          if (this.locomotiveScroll) {
            this.locomotiveScroll.destroy();
            this.locomotiveScroll = null;
          }
        }
      });
    }
  }

  destroy() {
    if (this.locomotiveScroll) {
      this.locomotiveScroll.destroy();
      this.locomotiveScroll = null;
      this.performanceMonitor = null;
      console.log('Locomotive Scroll: Destroyed');
    }

    // Clean up design mode observer
    if (this.designModeObserver) {
      this.designModeObserver.disconnect();
      this.designModeObserver = null;
      console.log('Locomotive Scroll: Design mode observer disconnected');
    }
  }
}

// Initialize when DOM is ready
let locomotiveManager;

function initLocomotiveScroll() {
  // Cleanup existing instance
  if (locomotiveManager) {
    locomotiveManager.destroy();
  }

  locomotiveManager = new LocomotiveScrollManager();
  locomotiveManager.init();
  
  // Make available globally for debugging
  window.locomotiveManager = locomotiveManager;
}

// Initialize based on document state
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initLocomotiveScroll);
} else {
  // DOM is already ready, initialize immediately
  initLocomotiveScroll();
}

// Enhanced section handling for Shopify theme editor
document.addEventListener('shopify:section:load', (event) => {
  setTimeout(() => {
    if (locomotiveManager && !locomotiveManager.isDesignMode) {
      // Only handle scroll elements when NOT in design mode
      const section = event.target;
      if (section && section.querySelectorAll('[data-scroll]').length > 0) {
        locomotiveManager.addScrollElements(section);
      }
      locomotiveManager.refresh();
    }
  }, 100);
});

document.addEventListener('shopify:section:unload', (event) => {
  setTimeout(() => {
    if (locomotiveManager && !locomotiveManager.isDesignMode) {
      // Only handle scroll elements when NOT in design mode
      const section = event.target;
      if (section && section.querySelectorAll('[data-scroll]').length > 0) {
        locomotiveManager.removeScrollElements(section);
      }
      locomotiveManager.refresh();
    }
  }, 100);
});

// Handle dynamic content updates (Ajax loading, etc.)
document.addEventListener('DOMSubtreeModified', (event) => {
  if (!locomotiveManager || !event.target) return;
  
  // Throttle to prevent excessive calls
  clearTimeout(locomotiveManager.dynamicUpdateTimer);
  locomotiveManager.dynamicUpdateTimer = setTimeout(() => {
    const target = event.target;
    if (target.nodeType === Node.ELEMENT_NODE && target.querySelectorAll('[data-scroll]').length > 0) {
      locomotiveManager.refresh();
    }
  }, 250);
});

// Modern alternative for DOMSubtreeModified (better performance)
if (window.MutationObserver) {
  const observer = new MutationObserver((mutations) => {
    if (!locomotiveManager) return;
    
    let shouldRefresh = false;
    mutations.forEach((mutation) => {
      if (mutation.type === 'childList') {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE && 
              (node.hasAttribute('data-scroll') || node.querySelectorAll('[data-scroll]').length > 0)) {
            shouldRefresh = true;
          }
        });
      }
    });
    
    if (shouldRefresh) {
      clearTimeout(locomotiveManager.mutationTimer);
      locomotiveManager.mutationTimer = setTimeout(() => {
        locomotiveManager.refresh();
      }, 250);
    }
  });
  
  // Start observing after locomotive is initialized
  setTimeout(() => {
    if (locomotiveManager) {
      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
    }
  }, 1000);
}

// Export for use in other scripts
export { LocomotiveScrollManager };
export default locomotiveManager;